// Shared band definitions and helpers for scale-type questions on a 0-10
// scale. Used by:
//   - src/components/insights/report.vue   (Culture Index Breakdown,
//                                           Most Recent Scores cell tinting)
//   - src/components/questions/standard.vue (Question Details modal)
//   - src/components/questions/custom.vue   (Question Details modal)
//
// Band `key` values correspond 1:1 to the .score-band-{key} CSS classes
// in src/assets/element-variables.scss (saturated fills) and to the
// SCORE_BAND_PASTELS map below (cell backgrounds).

export const NPS_BANDS = [
  { key: 'low',      label: 'Detractors (0-6)', min: 0, max: 6 },
  { key: 'moderate', label: 'Passives (7-8)',   min: 7, max: 8 },
  { key: 'promoter', label: 'Promoters (9-10)', min: 9, max: 10 },
];

// "Very High" replaces "Promoter" here because Promoter is NPS-specific
// jargon and confuses the reading for non-NPS questions.
export const SCALE_BANDS = [
  { key: 'zero',     label: 'Zero (0)',         min: 0, max: 0 },
  { key: 'low',      label: 'Low (1-3)',        min: 1, max: 3 },
  { key: 'moderate', label: 'Moderate (4-6)',   min: 4, max: 6 },
  { key: 'high',     label: 'High (7-8)',       min: 7, max: 8 },
  { key: 'promoter', label: 'Very High (9-10)', min: 9, max: 10 },
];

// Pastel companion palette for the saturated .score-band-{key} fills.
// Used for Most Recent Scores table cell backgrounds where text must stay
// legible on top (saturated would wash out the numeric scores).
export const SCORE_BAND_PASTELS = {
  zero:     '#F7CFD0',
  low:      '#FFEAEA',
  moderate: '#FFFBF2',
  high:     '#D9FDD8',
  promoter: '#ACFFAC',
};

export function isNpsType(type) {
  return type === 'NPS' || type === 'eNPS';
}

export function getBandsForQuestion(questionType) {
  return isNpsType(questionType) ? NPS_BANDS : SCALE_BANDS;
}

// Collapse a per-score histogram { 0: n, 1: n, ..., 10: n } into an array
// of band objects augmented with `count` and `pct`. Empty bands are kept so
// callers can render them as zero-width segments.
export function bucketScores(histogram, bands) {
  if (!histogram) return bands.map(b => ({ ...b, count: 0, pct: 0 }));
  const total = Object.values(histogram).reduce((a, b) => a + (b || 0), 0);
  return bands.map(b => {
    let count = 0;
    for (let s = b.min; s <= b.max; s++) count += histogram[s] || 0;
    return { ...b, count, pct: total ? (count / total) * 100 : 0 };
  });
}

// Returns the band key (e.g. 'promoter') for a single score value, given a
// question type. Useful for coloring a single value (table cell, chip) when
// the full distribution isn't needed.
export function bandKeyForScore(questionType, score) {
  if (score === null || score === undefined || Number.isNaN(score)) return null;
  const bands = getBandsForQuestion(questionType);
  for (var i = 0; i < bands.length; i++) {
    var b = bands[i];
    if (score >= b.min && score <= b.max) return b.key;
  }
  return null;
}

// Bucket a datamultichoices row into band segments for the Question Details
// stacked bar. Prefers the raw histogram; falls back to spreading the
// legacy 3-bucket range[] when the histogram hasn't landed yet. Returns []
// when there's nothing to render so the v-for can skip emission.
//
// TODO(remove-after-histogram-rollout): the range[] fallback path should go
// away once all callers of getDataScale consume the new histogram field.
export function resolveBandBuckets(row, bands) {
  if (!row) return [];
  if (row.histogram) return bucketScores(row.histogram, bands);
  const range = row.range || [];
  const r0 = range[0] || 0;
  const r1 = range[1] || 0;
  const r2 = range[2] || 0;
  const total = r0 + r1 + r2;
  if (!total) return [];
  const pct = (n) => (n / total) * 100;
  if (bands === NPS_BANDS) {
    // Legacy buckets already line up with NPS boundaries (0-6 / 7-8 / 9-10).
    return [
      { ...bands[0], count: r0, pct: pct(r0) },
      { ...bands[1], count: r1, pct: pct(r1) },
      { ...bands[2], count: r2, pct: pct(r2) },
    ];
  }
  // Scale 5-band: spread the 3 legacy buckets into low/high/promoter and
  // leave zero + moderate empty — they can't be reconstructed.
  return [
    { ...bands[0], count: 0,  pct: 0 },
    { ...bands[1], count: r0, pct: pct(r0) },
    { ...bands[2], count: 0,  pct: 0 },
    { ...bands[3], count: r1, pct: pct(r1) },
    { ...bands[4], count: r2, pct: pct(r2) },
  ];
}

// Pixel width for a stacked-bar segment representing `pct` of a container
// that is `widthPx` wide. 537 is the current Question Details bar width.
export function scaleBandWidth(pct, widthPx = 537) {
  if (!pct) return 'width: 0px;';
  return `width: ${Math.round((widthPx / 100) * pct)}px;`;
}
